/*
  No2moro Regional Pricing Prototype
  ----------------------------------
  One Shopify product, two underlying variants per physical size where needed.

  Standard example: UK12
  GB twin example: UK12 + invisible U+2060 WORD JOINER

  The shopper sees only UK12. The invisible marker exists only so Shopify can
  keep two distinct variant records with different prices while preserving the
  same product page and (later) the same SKU.

  Prototype overrides:
    ?n2m_region=gb
    ?n2m_region=ni
    ?n2m_region=standard

  Production geolocation can call:
    window.No2moroRegionalPricing.setRegion('gb' | 'standard')
*/
(() => {
  if (window.No2moroRegionalPricing) return;

  const MARKER = '\u2060';
  const STORAGE_KEY = 'no2moro_pricing_region_v2';
  const VALID = new Set(['gb', 'standard', 'ni']);
  let applying = false;

  const cleanValue = (value = '') => String(value).split(MARKER).join('');
  const isGbValue = (value = '') => String(value).includes(MARKER);
  const normalizeRegion = (region) => String(region || '').toLowerCase() === 'gb' ? 'gb' : 'standard';

  const cleanTextNodes = (node) => {
    if (!node) return;
    const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
    const nodes = [];

    while (walker.nextNode()) nodes.push(walker.currentNode);

    nodes.forEach((textNode) => {
      if (textNode.nodeValue && textNode.nodeValue.includes(MARKER)) {
        textNode.nodeValue = cleanValue(textNode.nodeValue);
      }
    });
  };

  const getQueryRegion = () => {
    const params = new URLSearchParams(window.location.search);

    // Manual region overrides are a preview/testing aid only.
    // Once this theme is live, customers cannot force a pricing region
    // by adding ?n2m_region=... to the URL.
    const isPreview =
      Boolean(window.Shopify && window.Shopify.designMode) ||
      params.has('preview_theme_id');

    if (!isPreview) return null;

    const value = params.get('n2m_region');
    return value && VALID.has(value.toLowerCase()) ? value.toLowerCase() : null;
  };

  const getStoredRegion = () => {
    try {
      const value = localStorage.getItem(STORAGE_KEY);
      return value && VALID.has(value) ? value : null;
    } catch (_) {
      return null;
    }
  };

  const getInitialRegion = () => {
    const queryRegion = getQueryRegion();
    if (queryRegion) {
      const normalizedQueryRegion = normalizeRegion(queryRegion);
      try { localStorage.setItem(STORAGE_KEY, normalizedQueryRegion); } catch (_) {}
      return normalizedQueryRegion;
    }

    const configured = window.No2moroRegionalPricingConfig || {};
    const country = String(configured.country || window.Shopify?.country || '').toUpperCase();

    // Ireland, EU, US, etc. always keep the existing Standard variant.
    if (country !== 'GB') return 'standard';

    // Only explicit NI classification may select a Standard variant while
    // Shopify is in its GB market. ASL's generic 'standard'/Zone One is not
    // proof of Northern Ireland (it can include mainland GB visitors).
    const geoRegion = String(window.No2moroGeoRegion || '').toLowerCase();
    if (geoRegion === 'ni') return 'standard';
    return 'gb';
  };

  let currentRegion = getInitialRegion();

  const rememberOriginalDisabled = (element) => {
    if (!element || element.dataset.no2moroOriginalDisabled !== undefined) return;
    element.dataset.no2moroOriginalDisabled = element.disabled ? 'true' : 'false';
  };

  const restoreOriginalDisabled = (element) => {
    if (!element) return false;
    return element.dataset.no2moroOriginalDisabled === 'true';
  };

  const setRegionVisibility = (element, show) => {
    if (!element) return;

    element.hidden = !show;
    element.dataset.no2moroRegionHidden = show ? 'false' : 'true';

    if (show) {
      element.style.removeProperty('display');
      element.style.removeProperty('visibility');
      element.removeAttribute('aria-hidden');
    } else {
      element.style.setProperty('display', 'none', 'important');
      element.style.setProperty('visibility', 'hidden', 'important');
      element.setAttribute('aria-hidden', 'true');
    }
  };

  const updatePrivateProperty = (form) => {
    if (!form) return;

    let field = form.querySelector('input[name="properties[_pricing_region]"]');

    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = 'properties[_pricing_region]';
      form.appendChild(field);
    }

    field.value = currentRegion === 'gb' ? 'GB' : 'STANDARD';
  };

  const regionForElement = (element, rawValue) => {
    const stored = element?.dataset?.no2moroPricingRegion;
    if (stored === 'gb' || stored === 'standard') return stored;
    return isGbValue(rawValue) ? 'gb' : 'standard';
  };

  const baseForElement = (element, rawValue) => {
    return element?.dataset?.no2moroBaseValue || cleanValue(rawValue).trim();
  };

  const applyRadioGroup = (variantSelects) => {
    const radios = Array.from(variantSelects.querySelectorAll('fieldset input[type="radio"]'));
    if (!radios.length) return;

    const groups = new Map();

    radios.forEach((input) => {
      const raw = input.value || '';
      const region = regionForElement(input, raw);
      const base = baseForElement(input, raw);

      input.dataset.no2moroPricingRegion = region;
      input.dataset.no2moroBaseValue = base;

      const label = input.id
        ? variantSelects.querySelector(`label[for="${CSS.escape(input.id)}"]`)
        : null;

      cleanTextNodes(label);

      if (!groups.has(base)) groups.set(base, []);
      groups.get(base).push({ input, label, region });
    });

    let replacement = null;

    groups.forEach((items) => {
      const hasGbTwin = items.some((item) => item.region === 'gb');
      if (!hasGbTwin) return;

      const desired = currentRegion === 'gb' ? 'gb' : 'standard';

      items.forEach((item) => {
        const show = item.region === desired;
        setRegionVisibility(item.input, show);
        setRegionVisibility(item.label, show);

        if (item.input.checked && !show) {
          replacement = items.find((candidate) => candidate.region === desired)?.input || replacement;
        }
      });
    });

    if (replacement && !replacement.checked) {
      replacement.checked = true;
      replacement.dispatchEvent(new Event('change', { bubbles: true }));
    }

    variantSelects.querySelectorAll('[data-selected-swatch-value]').forEach(cleanTextNodes);
  };

  const applyVariantSelects = (variantSelects) => {
    variantSelects.querySelectorAll('select').forEach((select) => {
      const options = Array.from(select.options);
      const groups = new Map();

      options.forEach((option) => {
        const raw = option.value || option.textContent || '';
        const region = regionForElement(option, raw);
        const base = baseForElement(option, raw);

        rememberOriginalDisabled(option);
        option.dataset.no2moroPricingRegion = region;
        option.dataset.no2moroBaseValue = base;

        if ((option.textContent || '').includes(MARKER)) {
          option.textContent = cleanValue(option.textContent || '');
        }

        if (!groups.has(base)) groups.set(base, []);
        groups.get(base).push({ option, region });
      });

      let replacement = null;

      groups.forEach((items) => {
        const hasGbTwin = items.some((item) => item.region === 'gb');
        if (!hasGbTwin) return;

        const desired = currentRegion === 'gb' ? 'gb' : 'standard';

        items.forEach((item) => {
          const show = item.region === desired;
          item.option.hidden = !show;
          item.option.disabled = !show || restoreOriginalDisabled(item.option);
          item.option.dataset.no2moroRegionHidden = show ? 'false' : 'true';

          if (item.option.selected && !show) {
            replacement = items.find((candidate) => candidate.region === desired)?.option || replacement;
          }
        });
      });

      if (replacement && !replacement.selected) {
        options.forEach((option) => option.removeAttribute('selected'));
        replacement.selected = true;
        replacement.setAttribute('selected', 'selected');
        select.value = replacement.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  };

  const applyVariantPicker = (variantSelects) => {
    if (!variantSelects) return;

    applyRadioGroup(variantSelects);
    applyVariantSelects(variantSelects);

    const productInfo = variantSelects.closest('product-info');
    if (productInfo) {
      productInfo.dataset.no2moroPricingRegion = currentRegion;
      productInfo
        .querySelectorAll('form[is="product-form"], form[action*="/cart/add"]')
        .forEach(updatePrivateProperty);
    }
  };

  const updateCardPrice = (root, regionButtons) => {
    const card = root.closest('.product-card');
    if (!card || !regionButtons.length) return;

    const pricedButton =
      regionButtons.find((button) => button.dataset.no2moroVariantPrice) ||
      regionButtons[0];

    const price = pricedButton?.dataset?.no2moroVariantPrice;
    const compare = pricedButton?.dataset?.no2moroVariantComparePrice;

    const currentPrice = card.querySelector('.no2moro-mobile-card-price__current');
    const comparePrice = card.querySelector('.no2moro-mobile-card-price__compare');
    const priceWrap = card.querySelector('.no2moro-mobile-card-price');

    if (currentPrice && price) currentPrice.textContent = price;

    if (comparePrice) {
      if (compare) {
        comparePrice.textContent = compare;
        comparePrice.hidden = false;
      } else {
        comparePrice.hidden = true;
      }
    }

    if (priceWrap) {
      priceWrap.dataset.no2moroPricingRegion = currentRegion;
    }
  };

  const applyProductCards = (scope = document) => {
    const roots = [];

    if (scope.matches?.('[data-no2moro-card-add]')) roots.push(scope);
    scope.querySelectorAll?.('[data-no2moro-card-add]').forEach((root) => roots.push(root));

    roots.forEach((root) => {
      const buttons = Array.from(root.querySelectorAll('[data-no2moro-card-size]'));
      if (!buttons.length) return;

      const groups = new Map();

      buttons.forEach((button) => {
        const raw = button.dataset.no2moroBaseValue || button.textContent || '';
        const region = regionForElement(button, button.textContent || '');
        const base = baseForElement(button, raw);

        button.dataset.no2moroPricingRegion = region;
        button.dataset.no2moroBaseValue = base;

        if ((button.textContent || '').trim() !== base) {
          button.textContent = base;
        }

        if (!groups.has(base)) groups.set(base, []);
        groups.get(base).push({ button, region });
      });

      const visibleRegionButtons = [];

      groups.forEach((items) => {
        const hasGbTwin = items.some((item) => item.region === 'gb');

        if (!hasGbTwin) {
          items.forEach((item) => visibleRegionButtons.push(item.button));
          return;
        }

        const desired = currentRegion === 'gb' ? 'gb' : 'standard';

        items.forEach((item) => {
          const show = item.region === desired;
          setRegionVisibility(item.button, show);
          if (show) visibleRegionButtons.push(item.button);
        });
      });

      updatePrivateProperty(root.querySelector('form'));
      updateCardPrice(root, visibleRegionButtons);
      root.dataset.no2moroPricingRegion = currentRegion;
    });
  };

  const applyStickyAtc = (scope = document) => {
    const bars = [];

    if (scope.matches?.('sticky-atc-bar')) bars.push(scope);
    scope.querySelectorAll?.('sticky-atc-bar').forEach((bar) => bars.push(bar));

    bars.forEach((bar) => {
      const select = bar.querySelector('select[name="id"]');
      if (!select) {
        updatePrivateProperty(bar.querySelector('form'));
        return;
      }

      const options = Array.from(select.options);
      const groups = new Map();

      options.forEach((option) => {
        const rawText = option.dataset.no2moroRawTitle || option.textContent || '';
        const region = regionForElement(option, rawText);
        const base = baseForElement(option, rawText);

        rememberOriginalDisabled(option);
        option.dataset.no2moroRawTitle = rawText;
        option.dataset.no2moroPricingRegion = region;
        option.dataset.no2moroBaseValue = base;

        if ((option.textContent || '').includes(MARKER)) {
          option.textContent = cleanValue(option.textContent || '');
        }

        if (!groups.has(base)) groups.set(base, []);
        groups.get(base).push({ option, region });
      });

      let replacement = null;

      groups.forEach((items) => {
        const hasGbTwin = items.some((item) => item.region === 'gb');
        if (!hasGbTwin) return;

        const desired = currentRegion === 'gb' ? 'gb' : 'standard';

        items.forEach((item) => {
          const show = item.region === desired;
          item.option.hidden = !show;
          item.option.disabled = !show || restoreOriginalDisabled(item.option);
          item.option.dataset.no2moroRegionHidden = show ? 'false' : 'true';

          if (item.option.selected && !show) {
            replacement = items.find((candidate) => candidate.region === desired)?.option || replacement;
          }
        });
      });

      if (replacement && !replacement.selected) {
        options.forEach((option) => option.removeAttribute('selected'));
        replacement.selected = true;
        replacement.setAttribute('selected', 'selected');
        select.value = replacement.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      updatePrivateProperty(bar.querySelector('form'));
      bar.dataset.no2moroPricingRegion = currentRegion;
    });
  };

  const applyAll = (scope = document) => {
    if (applying) return;
    applying = true;

    try {
      if (scope.matches?.('variant-selects')) applyVariantPicker(scope);
      scope.querySelectorAll?.('variant-selects').forEach(applyVariantPicker);

      applyProductCards(scope);
      applyStickyAtc(scope);

      document.documentElement.dataset.no2moroPricingRegion = currentRegion;

      window.dispatchEvent(new CustomEvent('no2moro:pricing-region-applied', {
        detail: { region: currentRegion }
      }));
    } finally {
      applying = false;
    }
  };

  const setRegion = (region, options = {}) => {
    const market = String(window.No2moroRegionalPricingConfig?.country ||
      window.Shopify?.country || '').toUpperCase();
    const explicitNI = String(window.No2moroGeoRegion || '').toLowerCase() === 'ni';
    // Do not let generic Zone One replace the GB twin during ASL startup.
    currentRegion = market === 'GB' && !explicitNI
      ? 'gb' : normalizeRegion(region);

    if (options.persist !== false) {
      try {
        localStorage.setItem(STORAGE_KEY, currentRegion);
      } catch (_) {}
    }

    applyAll(document);

    window.dispatchEvent(new CustomEvent('no2moro:pricing-region-change', {
      detail: { region: currentRegion }
    }));
  };

  window.No2moroRegionalPricing = {
    marker: MARKER,
    cleanValue,
    getRegion: () => currentRegion,
    setRegion,
    apply: applyAll
  };

  const start = () => {
    applyAll(document);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          requestAnimationFrame(() => applyAll(node));
        });
      });
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
